import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { 
  BellIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import UserAvatar from './UserAvatar';
import PointsBalanceBadge from './PointsBalanceBadge';
import TopBarStats from './TopBarStats';
import { dropdownPanelVariants, dropdownTransition } from './dropdownMotion';
import { titleSwap, EASE_SMOOTH } from '../../styles/motion';
import { DROPDOWN_Z_INDEX } from '../../hooks/useFloatingPanelPosition';

function TopBar({ title, subtitle, onNotificationClick, notificationsCount, searchValue, onSearchChange, onMenuClick, menuOpen = false, onSettingsClick, pointsBalance, hubStats }) {
  const { user, logout } = useAuth();
  const { t, formatMessage } = useLanguage();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const reducedMotion = useReducedMotion();
  const menuTransition = dropdownTransition(reducedMotion);

  useEffect(() => {
    const main = document.getElementById('main-content');
    const target = main || window;
    const onScroll = () => {
      const top = target === window ? window.scrollY : target.scrollTop;
      setScrolled(top > 8);
    };
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => target.removeEventListener('scroll', onScroll);
  }, []);

  const handleSettingsClick = () => {
    setShowUserMenu(false);
    if (onSettingsClick) {
      onSettingsClick();
    }
  };

  const userRoleLabel = user?.is_admin && !user?.company_id
    ? (t.platformAdmin?.superAdminBadge || t.common.admin)
    : user?.is_admin
    ? t.common.admin
    : user?.is_vendor
      ? t.common.vendor
      : t.common.member;

  const userMenuItems = [
    { icon: Cog6ToothIcon, label: t.topBar.settings, action: handleSettingsClick },
    { type: 'divider' },
    { icon: ArrowRightOnRectangleIcon, label: t.topBar.signOut, action: logout, danger: true }
  ];

  return (
    <div className={`topbar-blend sticky top-0 z-[9998] h-14 transition-[background-color,box-shadow,border-color] duration-500 ease-out max-lg:safe-top sm:h-16 ${
      scrolled
        ? 'bg-white/95 shadow-soft backdrop-blur-2xl '
        : 'bg-white/75 backdrop-blur-xl '
    }`}>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent " aria-hidden="true" />
      <div className="relative h-full px-4 sm:px-6 flex items-center justify-between">
        {/* Mobile Menu Button */}
        <motion.button
          type="button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          animate={{ rotate: menuOpen ? 90 : 0 }}
          transition={reducedMotion ? undefined : { type: 'spring', stiffness: 400, damping: 28 }}
          onClick={onMenuClick}
          aria-label={menuOpen ? (t.topBar?.closeMenu || t.common?.closeMenu) : (t.topBar?.openMenu || t.common?.openMenu)}
          aria-expanded={menuOpen}
          aria-controls="app-sidebar"
          className="max-lg:touch-target inline-flex items-center justify-center lg:hidden mr-2 shrink-0 rounded-xl border border-white/30 bg-white/40 p-2 shadow-soft backdrop-blur-sm transition-[background-color,border-color,box-shadow,transform] duration-300 hover:bg-white/60    sm:mr-3"
        >
          <Bars3Icon className="w-5 h-5 text-secondary-600 " aria-hidden="true" />
        </motion.button>

        {/* Left Section - Title */}
        <div className="flex min-w-0 flex-1 items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${title}-${subtitle || ''}`}
              initial={reducedMotion ? 'initialFade' : 'initial'}
              animate="animate"
              exit={reducedMotion ? 'exitFade' : 'exit'}
              variants={reducedMotion ? { initialFade: { opacity: 0 }, animate: { opacity: 1 }, exitFade: { opacity: 0 } } : titleSwap}
              transition={{ duration: 0.28, ease: EASE_SMOOTH }}
              className="min-w-0"
            >
              <h1 className="truncate text-base font-semibold text-secondary-900  sm:text-xl sm:font-bold sm:tracking-tight">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-0.5 truncate text-xs leading-5 text-secondary-500  sm:mt-1 sm:text-sm">
                  {subtitle}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>


        {/* Right Section - Actions */}
        <div className="flex shrink-0 items-center justify-end gap-1.5 sm:gap-2 md:space-x-4">
          <TopBarStats stats={hubStats} />

          {typeof pointsBalance === 'number' ? (
            <PointsBalanceBadge
              value={pointsBalance}
              ariaLabel={formatMessage(t.topBar.pointsBalance, { count: pointsBalance })}
            />
          ) : null}

          {/* Notifications */}
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onNotificationClick?.()}
            aria-label={t.topBar?.notifications || t.notifications?.title || 'Notifications'}
            className="max-lg:touch-target relative inline-flex items-center justify-center rounded-xl border border-white/30 bg-white/40 p-2 shadow-soft backdrop-blur-sm transition-[background-color,border-color,box-shadow,transform] duration-300 hover:bg-white/60 hover:shadow-medium group    sm:rounded-2xl sm:p-3"
          >
            <BellIcon className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600 group-hover:text-orange-600 transition-colors  " />
            {notificationsCount > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-error-500 to-error-600 rounded-full flex items-center justify-center shadow-glow"
              >
                <span className="text-2xs font-bold text-white">
                  {notificationsCount > 99 ? '99+' : notificationsCount}
                </span>
              </motion.div>
            )}
          </motion.button>

          {/* User Menu */}
          <div className={`relative ${showUserMenu ? 'z-[10020]' : ''}`}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="max-lg:touch-target inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/30 bg-white/40 p-1.5 shadow-soft backdrop-blur-sm transition-[background-color,border-color,box-shadow,transform] duration-300 hover:bg-white/60 hover:shadow-medium group    sm:gap-2 sm:space-x-2 sm:rounded-2xl sm:p-2"
            >
              <div className="relative">
                <UserAvatar
                  src={user?.avatar_url}
                  alt={user?.username || t.common.user}
                  size="xs"
                  variant="brand"
                />
                <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 sm:w-3 sm:h-3 bg-success-500 rounded-full border border-white  shadow-soft"></div>
              </div>
              
              <div className="hidden sm:flex flex-col items-start">
                <span className="text-sm font-medium text-secondary-900  truncate max-w-24">
                  {user?.username || t.common.user}
                </span>
                <span className="text-2xs text-secondary-500 ">
                  {userRoleLabel}
                </span>
              </div>
              
              <ChevronDownIcon className={`hidden h-4 w-4 text-secondary-400 transition-transform duration-200  sm:block ${showUserMenu ? 'rotate-180' : ''}`} />
            </motion.button>

            {/* User Dropdown Menu */}
            <AnimatePresence>
              {showUserMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-[10019]" 
                    onClick={() => setShowUserMenu(false)}
                  />
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={dropdownPanelVariants}
                    transition={menuTransition}
                    style={{ zIndex: DROPDOWN_Z_INDEX }}
                    className="dropdown-panel-ios absolute right-0 top-full mt-1 w-[min(16rem,calc(100vw-2rem))] origin-top overflow-hidden p-0 sm:w-64"
                  >
                    {/* User Info Header */}
                    <div className="p-4 border-b border-white/20 bg-gradient-to-r from-orange-50/50 to-amber-50/50   ">
                      <div className="flex items-center space-x-3">
                        <UserAvatar
                          src={user?.avatar_url}
                          alt={user?.username || t.common.user}
                          size="sm"
                          variant="brand"
                          className="shadow-glow"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-secondary-900  truncate">
                            {user?.username || t.common.user}
                          </p>
                          <p className="text-2xs text-secondary-500  truncate">
                            {user?.email || 'user@example.com'}
                          </p>
                          {user?.is_admin && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-2xs font-medium bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-700 border border-amber-200/50 mt-1  ">
                              {t.common.administrator}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="p-2">
                      {userMenuItems.map((item, index) => {
                        if (item.type === 'divider') {
                          return (
                            <div key={index} className="divider-soft my-2" />
                          );
                        }

                        const Icon = item.icon;
                        return (
                          <motion.button
                            key={index}
                            onClick={item.action}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-[color,background-color,transform] duration-200 ${
                              item.danger 
                                ? 'text-error-600 hover:bg-error-50 hover:text-error-700  ' 
                                : 'text-secondary-600 hover:bg-white/60 hover:text-secondary-900   '
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                            <span>{item.label}</span>
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TopBar;
